import { Alert, Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { useRef, useState } from 'react';
import { ChipRail, ColLabel, ConsoleTable, Pill, Row, SectionCard, TableMessage } from '../../ui';
import { tokens as T } from '../../theme/tokens';
import { memoryTitle } from '../../data/memoryState';
import { useUIStore } from '../../stores/uiStore';
import { getSources } from '../../stores/sources';
import { useConsolePrefix } from '../../shell/nav';
import { ApiError } from '../../api/client';
import type { Memory, MemoryRecallResult, MemoryRelevance } from '../../data/types';

const COLS = [
  { key: 'rank', label: '#', w: 44 },
  { key: 'score', label: 'Score', w: 80 },
  { key: 'index', label: 'Index rank', w: 100 },
  { key: 'kind', label: 'Kind', w: 100 },
  { key: 'scope', label: 'Scope', w: 150 },
  { key: 'text', label: 'Text' },
];

/** The recall debugger: the SAME pipeline the data plane runs — the index's
 *  hybrid ranking (BM25 blended with meaning) at the chosen relevance cutoff,
 *  hydrated and post-filtered — with the evidence behind every place: the
 *  index's score and the hit's rank in the index's own answer. Recall is
 *  index-only (DR-85). It spends one of the index's search requests and
 *  writes nothing. Results are component-local, guarded by a request
 *  generation; a hit not on the loaded page opens in the drawer through
 *  `onHits`. */
export function RecallPanel({
  estateId,
  tenantId,
  onHits,
}: {
  estateId: string;
  tenantId: string;
  onHits: (byId: Record<string, Memory>) => void;
}) {
  const ui = useUIStore();
  const harness = useConsolePrefix() === '/dev';
  const src = getSources().live;
  const live = !harness && src.kind === 'live';
  const [scope, setScope] = useState('');
  const [k, setK] = useState<'5' | '10' | '20'>('10');
  const [relevance, setRelevance] = useState<MemoryRelevance>('lowest');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MemoryRecallResult | null>(null);
  const gen = useRef(0);

  async function run() {
    const q = ui.memoryRecallQuery.trim();
    if (!q || busy) return;
    const mine = ++gen.current;
    setBusy(true);
    setError(null);
    try {
      const scopes = scope.trim() ? [scope.trim()] : [];
      // Through the source, never a fixture import: the harness's scripted
      // recall lives behind the /dev dynamic import, and the authed console
      // must not bundle it.
      const res = await src.recallMemories(estateId, tenantId, { query: q, scopes, k: Number(k), relevance });
      if (mine !== gen.current) return;
      setResult(res);
      const byId: Record<string, Memory> = {};
      for (const h of res.hits) byId[h.memory.id] = h.memory;
      onHits(byId);
    } catch (err) {
      if (mine !== gen.current) return;
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      if (mine === gen.current) setBusy(false);
    }
  }

  const canRun = Boolean(ui.memoryRecallQuery.trim()) && !busy && src.kind !== 'empty';

  return (
    <SectionCard p="18px 20px">
      <Group justify="space-between" align="center" wrap="wrap" gap={12}>
        <ColLabel>Recall debugger</ColLabel>
        <Text fz={12} c={T.ink4}>
          Runs the data plane's own recall. Spends one index search request; writes nothing.
        </Text>
      </Group>
      <Group mt={12} gap={10} wrap="wrap" align="flex-end">
        <TextInput
          label="Query"
          value={ui.memoryRecallQuery}
          onChange={(e) => ui.set('memoryRecallQuery', e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void run();
          }}
          placeholder="what an agent would ask"
          style={{ flex: '1 1 320px' }}
          styles={{ input: { fontFamily: T.fontMono } }}
        />
        <TextInput
          label="Scope"
          value={scope}
          onChange={(e) => setScope(e.currentTarget.value)}
          placeholder="global"
          style={{ flex: '0 1 200px' }}
          styles={{ input: { fontFamily: T.fontMono } }}
        />
        <ChipRail
          value={k}
          onChange={setK}
          size="sm"
          data={[
            { value: '5', label: '5' },
            { value: '10', label: '10' },
            { value: '20', label: '20' },
          ]}
        />
        <ChipRail
          value={relevance}
          onChange={setRelevance}
          size="sm"
          data={[
            { value: 'lowest', label: 'lowest' },
            { value: 'low', label: 'low' },
            { value: 'medium', label: 'medium' },
            { value: 'high', label: 'high' },
          ]}
        />
        <Button
          color="dark"
          loading={busy}
          disabled={!canRun}
          title={!live && !harness ? 'not connected to a live memory store' : undefined}
          onClick={() => void run()}
        >
          Run recall
        </Button>
      </Group>
      {error ? (
        <Alert color="red" mt={12} radius="lg" title="Recall failed">
          {error}
        </Alert>
      ) : null}
      {result ? (
        <Stack gap={8} mt={14}>
          <Text fz={12} c={T.ink4}>
            {result.hits.length} hit{result.hits.length === 1 ? '' : 's'} across {result.scopes.join(', ')} — index {result.indexHits} candidate
            {result.indexHits === 1 ? '' : 's'} in {result.indexMs} ms at relevance {result.relevance}. The score is the index's (a relevance
            score when the serving config returns one, a rank fallback otherwise).
          </Text>
          <ConsoleTable cols={COLS} minWidth={720}>
            {result.hits.length === 0 ? (
              <TableMessage
                span={COLS.length}
                title="No hits"
                detail="An honest empty answer: nothing in these scopes passed the cutoff, or the index has not ingested it yet."
              />
            ) : (
              result.hits.map((h) => (
                <Row key={h.memory.id} onClick={() => ui.selectMemory(h.memory.id)} selected={ui.memorySelection === h.memory.id}>
                  <Table.Td>{h.rank}</Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz={12}>
                      {h.score.toFixed(3)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz={12}>
                      {h.indexRank ? `#${h.indexRank}` : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Pill tone="neutral" size="sm">
                      {h.memory.kind || 'fact'}
                    </Pill>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz={12} truncate>
                      {h.memory.scope}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={13} truncate>
                      {memoryTitle(h.memory, 110)}
                    </Text>
                  </Table.Td>
                </Row>
              ))
            )}
          </ConsoleTable>
        </Stack>
      ) : null}
    </SectionCard>
  );
}
